import { t as msg } from "./lib/i18n";
import { useLanguage, setLanguage, initializeLanguage, languagePending, languageChoiceVersion, initialLanguageError } from "./lib/i18n";
import React, { useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { AppNavigation } from "./components/AppNavigation";
import type { Template, User } from "../../../packages/shared/src/types";
import { api, ApiError } from "./lib/api";
import { Support } from "./pages/Support";
import { Legal } from "./pages/Legal";
import { Profile } from "./pages/Profile";
import { GuestData } from "./pages/GuestData";
import { Friends } from "./pages/Friends";
import { Login } from "./components/Login";
import { Home } from "./pages/Home";
import { Templates } from "./pages/Templates";
import { Editor } from "./pages/Editor";
import { MyCards } from "./pages/MyCards";
import { Receive } from "./pages/Receive";
import "./style.css";
type AccountState =
  | { status: "checking"; user: User | null }
  | { status: "authenticated"; user: User }
  | { status: "anonymous"; user: null }
  | { status: "error"; user: User | null; message: string };
function App() {
  const {locale} = useLanguage();
  const [templates, setTemplates] = useState<Template[]>([]),
    [templatesLoading, setTemplatesLoading] = useState(true),
    [account, setAccount] = useState<AccountState>({
      status: "checking",
      user: null,
    }),
    [showLogin, setShowLogin] = useState(false),
    [local, setLocal] = useState(false),
    [error, setError] = useState(initialLanguageError()),
    [signingOut, setSigningOut] = useState(false);
  const path = location.pathname;
  const sharedCard = /^\/c\/[^/]+$/.test(path);
  const accountRequest = useRef(0);
  const user = account.user,
    userLoading = account.status === "checking";
  const userError = account.status === "error" ? account.message : "";
  function clearAccount() {
    accountRequest.current++;
    setAccount({ status: "anonymous", user: null });
  }
  async function loadUser() {
    const request = ++accountRequest.current;
    const expectedLanguageChoice = languageChoiceVersion();
    setAccount((previous) => ({ status: "checking", user: previous.user }));
    try {
      const r = await api<{ user: User }>("/api/me");
      if (request !== accountRequest.current) return;
      // A profile read started before a manual choice cannot undo that choice,
      // even when it arrives after the preference write has completed.
      if(!languagePending() && expectedLanguageChoice === languageChoiceVersion()) {
        try { await setLanguage(r.user.preferredLocale as Parameters<typeof setLanguage>[0], 'account'); }
        catch (e) { if(request === accountRequest.current)setError((e as Error).message); }
      }
      if (request !== accountRequest.current) return;
      setAccount({ status: "authenticated", user: r.user });
    } catch (e) {
      if (request !== accountRequest.current) return;
      if (e instanceof ApiError && e.status === 401) {
        setAccount({ status: "anonymous", user: null });
        if (path === "/app/sign-in") setShowLogin(true);
      } else {
        setAccount((previous) => ({
          status: "error",
          user: previous.user,
          message: msg("We couldn’t check your account. Please try again."),
        }));
      }
    }
  }
  useEffect(() => {
    void loadUser();
    api<{ local: boolean }>("/api/config")
      .then((r) => setLocal(r.local))
      .catch(() => {});
  }, []);
  useEffect(() => {
    let live=true;
    setTemplatesLoading(true);
    api<{templates:Template[]}>("/api/templates").then(r=>{if(live)setTemplates(r.templates)}).catch(e=>{if(live)setError(e.message)}).finally(()=>{if(live)setTemplatesLoading(false)});
    return ()=>{live=false};
  },[locale]);
  const signedIn = () => {
    setShowLogin(false);
    clearAccount();
    void loadUser();
  };
  async function signOut() {
    setSigningOut(true);
    setError("");
    try {
      await api("/api/auth/logout", "POST", {});
      clearAccount();
      location.assign("/");
    } catch (e) {
      setError((e as Error).message);
      setSigningOut(false);
    }
  }
  let content: React.ReactNode;
  if (path === "/")
    content = <Home templates={templates} loading={templatesLoading} />;
  else if (path === "/app/sign-in")
    content = (
      <section className="wrap section narrow">
        <p className="eyebrow">{msg("A PROMISE CARD FOR IOS")}</p>
        <h1 className="page-title">{msg("Your promises, on your phone.")}</h1>
        <p>
          {msg("Sign in to the same account you use on the web. This creates a separate session for the app.")}</p>
        {userLoading ? (
          <p role="status">{msg("Checking your account…")}</p>
        ) : userError ? (
          <>
            <p role="alert">{userError}</p>
            <button className="button" onClick={() => void loadUser()}>
              {msg("Try again")}</button>
          </>
        ) : user ? (
          <>
            <a
              className="button"
              href={`/auth/native/finish?flow=${encodeURIComponent(new URLSearchParams(location.search).get("flow") || "")}`}
            >
              {msg("Continue as")}{user.displayName || msg("your account")}
            </a>
            <button className="text-button" onClick={() => setShowLogin(true)}>
              {msg("Use another account")}</button>
          </>
        ) : (
          <button className="button" onClick={() => setShowLogin(true)}>
            {msg("Sign in")}</button>
        )}
      </section>
    );
  else if (path === "/templates" || (path === "/create" && !new URLSearchParams(location.search).has("draft") && !new URLSearchParams(location.search).has("template")))
    content = <Templates key={user?.id || "guest"} templates={templates} loading={templatesLoading} user={user} accountReady={!userLoading && !userError} />;
  else if (path === "/create")
    content = (
      <Editor
        templates={templates}
        user={user}
        signIn={() => setShowLogin(true)}
        accountReady={!userLoading || !!user}
        signInOpen={showLogin}
      />
    );
  else if (
    path === "/cards" ||
    path === "/profile" ||
    path.startsWith("/friends")
  ) {
    content =
      userLoading && !user ? (
        <section className="wrap section">
          <p className="loading-state" role="status">
            {msg("Checking your account…")}</p>
        </section>
      ) : userError ? (
        <section className="wrap section narrow">
          <p role="alert" className="error">
            {userError}
          </p>
          <button className="button" onClick={() => void loadUser()}>
            {msg("Try again")}</button>
        </section>
      ) : path === "/cards" ? (
        <MyCards
          key={user?.id || "anonymous"}
          user={user}
          signIn={() => setShowLogin(true)}
        />
      ) : path.startsWith("/friends") ? (
        <Friends
          key={user?.id || "anonymous"}
          user={user}
          signIn={() => setShowLogin(true)}
        />
      ) : (
        <Profile
          key={user?.id || "anonymous"}
          user={user}
          signIn={() => setShowLogin(true)}
          reloadUser={loadUser}
          signOut={signOut}
          signingOut={signingOut}
          onDeleted={clearAccount}
        />
      );
  } else if (/^\/c\/[^/]+$/.test(path))
    content = (
      <Receive
        key={user?.id || "anonymous"}
        id={path.split("/")[2]}
        user={user}
        signIn={() => setShowLogin(true)}
        accountReady={account.status === "authenticated" || account.status === "anonymous"}
        accountError={userError}
        retryAccount={() => void loadUser()}
      />
    );
  else if (/^\/cards\/[^/]+$/.test(path))
    content = userLoading ? (
      <section className="wrap section">
        <p role="status">{msg("Checking your account…")}</p>
      </section>
    ) : (
      <Receive
        key={user?.id || "anonymous"}
        id={path.split("/")[2]}
        user={user}
        signIn={() => setShowLogin(true)}
        accountReady={account.status === "authenticated" || account.status === "anonymous"}
        accountError={userError}
        retryAccount={() => void loadUser()}
      />
    );
  else if (path === "/dev/inbox") content = <DevInbox />;
  else if (path === "/contact")
    content = <Support accountId={user?.id} />;
  else if (path === "/terms" || path === "/privacy")
    content = <Legal privacy={path === "/privacy"} />;
  else if (path === "/guest-data")
    content = <GuestData signIn={() => setShowLogin(true)} />;
  else
    content = (
      <section className="wrap section narrow">
        <h1 className="page-title">{msg("A little off the beaten path.")}</h1>
        <p>{msg("We couldn’t find this page.")}</p>
        <a href="/" className="button">
          {msg("Back to home")}</a>
      </section>
    );
  return (
    <>
      <a className="skip-link" href="#main">
        {msg("Skip to content")}</a>
      <AppNavigation path={path} user={user} focused={sharedCard} />
      <main id="main" tabIndex={-1}>
        {error && (
          <div className="wrap">
            <p className="error" role="alert">
              {error}
            </p>
          </div>
        )}
        {content}
      </main>
      {path !== "/" ? <footer className="footer footer-focused wrap"><nav aria-label={msg("Explore and support")}><a href="/contact">{msg("Contact")}</a><a href="/privacy">{msg("Privacy")}</a><a href="/terms">{msg("Terms")}</a></nav></footer> : <footer className="footer wrap">
        <a className="brand" href="/">
          a promise card<span className="brand-dot">.</span>
        </a>
        <span>
          {msg("Official site: apromisecard.com")}<br />
          <small>{msg("Emails from hello@mail.apromisecard.com")}</small><br />
          <small><a href="/terms">{msg("Terms update · effective October 16, 2026")}</a></small>
        </span>
        <div>
          <a href="/contact">{msg("Contact")}</a>
          <a href="/privacy">{msg("Privacy")}</a>
          {user || account.status === "anonymous" ? (
            <a href={user ? "/profile#data" : "/guest-data"}>{msg("Your data")}</a>
          ) : (
            <span aria-busy={userLoading}>{msg("Your data")}</span>
          )}
          <a href="/terms">{msg("Terms")}</a>
          {user && (
            <button
              className="text-button"
              disabled={signingOut}
              onClick={signOut}
            >
              {signingOut ? msg("Signing out…") : msg("Sign out")}
            </button>
          )}
          {local && <a href="/dev/inbox">{msg("Dev inbox")}</a>}
        </div>
      </footer>}
      {showLogin && (
        <Login
          local={local}
          preferredMethod={path === "/app/sign-in" ? "email" : undefined}
          onClose={() => setShowLogin(false)}
          onSuccess={signedIn}
        />
      )}
    </>
  );
}
function DevInbox() {
  const [messages, setMessages] = useState<
      Array<{
        id: string;
        to: string;
        subject: string;
        text: string;
        url?: string;
        status: string;
      }>
    >([]),
    [error, setError] = useState(initialLanguageError());
  const refresh = () =>
    api<{ messages: typeof messages }>("/api/dev/inbox")
      .then((r) => setMessages(r.messages))
      .catch((e) => setError(e.message));
  useEffect(() => {
    refresh();
  }, []);
  return (
    <section className="wrap section narrow">
      <p className="eyebrow">{msg("LOCAL DEVELOPMENT ONLY")}</p>
      <h1 className="page-title">{msg("Development inbox")}</h1>
      <p>
        {msg("Messages stay on this machine. Nothing is sent to a real email address.")}</p>
      <button className="button secondary" onClick={refresh}>
        {msg("Refresh inbox")}</button>
      {error && <p className="error">{error}</p>}
      {messages.map((m) => (
        <article key={m.id} className="inbox-message">
          <small>
            {m.to} · {m.status}
          </small>
          <h2>{m.subject || msg("An expired or used message")}</h2>
          <p>{m.text}</p>
          {m.url && (
            <a className="button" href={m.url}>
              {msg("Open confirmation")}</a>
          )}
        </article>
      ))}
    </section>
  );
}
initializeLanguage().catch(()=>{}).finally(()=>createRoot(document.getElementById("root")!).render(<App />));
